/**
 * InAppToast — animated in-app notification banner.
 * Slides down from top, auto-dismisses after 4 seconds.
 * Used for foreground notifications when user is NOT on the chat screen.
 */

import React, { useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Image,
} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  runOnJS,
  Easing,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useToastStore } from '@/stores/toastStore';
import { useTheme } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS, SHADOW } from '../../utils/design-tokens';

export default function InAppToast() {
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const toast = useToastStore((s) => s.toast);
  const dismissToast = useToastStore((s) => s.dismissToast);

  const translateY = useSharedValue(-120);

  useEffect(() => {
    if (toast) {
      translateY.value = withTiming(0, { duration: 300, easing: Easing.out(Easing.cubic) });
    } else {
      translateY.value = withTiming(-120, { duration: 250, easing: Easing.in(Easing.cubic) });
    }
  }, [toast]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  if (!toast) return null;

  const handlePress = () => {
    toast.onPress?.();
    dismissToast();
  };

  return (
    <Animated.View
      style={[
        styles.container,
        { top: insets.top + (Platform.OS === 'android' ? 4 : 0) },
        animatedStyle,
      ]}
    >
      <TouchableOpacity
        style={[styles.content, { backgroundColor: colors.surface, borderColor: colors.border }]}
        onPress={handlePress}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel={toast.body}
      >
        <View style={styles.iconWrap}>
          <Image source={require('../../../assets/logo.png')} style={{ width: 22, height: 22, resizeMode: 'contain' }} />
        </View>
        <View style={styles.textWrap}>
          <Text style={[styles.title, { color: colors.text }]} numberOfLines={1}>
            {toast.title}
          </Text>
          <Text style={[styles.body, { color: colors.textSecondary }]} numberOfLines={2}>
            {toast.body}
          </Text>
        </View>
        <TouchableOpacity
          onPress={dismissToast}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityLabel="Dismiss"
        >
          <Ionicons name="close" size={18} color={colors.textDim} />
        </TouchableOpacity>
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: SPACING.md,
    right: SPACING.md,
    zIndex: 9999,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: RADIUS.lg,
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.md,
    gap: SPACING.md,
    ...SHADOW.elevated,
    borderWidth: 0.5,
    borderColor: 'rgba(33, 150, 243, 0.12)',
  },
  iconWrap: {
    width: SPACING['2xl'],
    height: SPACING['2xl'],
    borderRadius: SPACING.base,
    backgroundColor: '#FF9500',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textWrap: {
    flex: 1,
  },
  title: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: '#1A202C',
    letterSpacing: 0.2,
  },
  body: {
    fontSize: FONT_SIZE.xs,
    color: '#64748B',
    marginTop: 1,
    lineHeight: 16,
  },
});
